import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Send,
  Wallet,
  BarChart3,
  Users,
  Filter,
  FileText,
  UsersRound,
  Plug,
  Settings,
  X,
  ChevronRight,
  Gift,
  History,
  BookOpen,
  Star
} from 'lucide-react';
import { BUILD_INFO } from '@/buildInfo';
import { useOnboarding } from '@/contexts/OnboardingContext';

/**
 * Sidebar - Customer dashboard navigation
 * 
 * RESPONSIBILITIES:
 * - Display navigation links
 * - Highlight active route
 * - Mobile responsive (overlay)
 * - Show build version
 * 
 * DOES NOT:
 * - Fetch data
 * - Make API calls
 * - Handle auth
 */

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Wallet', href: '/dashboard/wallet', icon: Wallet },
  { name: 'Campaigns', href: '/dashboard/campaigns', icon: Send },
  { name: 'Delivery Logs', href: '/dashboard/history', icon: History },
  { name: 'Analytics', href: '/dashboard/analytics', icon: BarChart3 },
  { name: 'Customers', href: '/dashboard/customers', icon: Users },
  { name: 'Segments', href: '/dashboard/segments', icon: Filter },
  { name: 'Templates', href: '/dashboard/templates', icon: FileText },
  { name: 'Google Reviews', href: '/dashboard/reviews', icon: Star },
  { name: 'Team', href: '/dashboard/team', icon: UsersRound },
  { name: 'Integrations', href: '/dashboard/integrations', icon: Plug },
  { name: 'Settings', href: '/dashboard/settings', icon: Settings },
];

export default function Sidebar({ isOpen, onClose }) {
  const location = useLocation();
  const { openGuide } = useOnboarding();

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const formatBuildTime = (isoString) => {
    try {
      return new Date(isoString).toLocaleString('en-AU', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '';
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden" 
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#f97316" viewBox="0 0 256 256">
                <path d="M169.57,72.59A80,80,0,0,0,16,104v64a16,16,0,0,0,16,16H86.67A80.15,80.15,0,0,0,160,232h64a16,16,0,0,0,16-16V152A80,80,0,0,0,169.57,72.59ZM32,104a64,64,0,1,1,64,64H32ZM224,216H160a64.14,64.14,0,0,1-55.68-32.43A79.93,79.93,0,0,0,174.7,89.71,64,64,0,0,1,224,152Z"></path>
              </svg>
              <span className="text-xl font-bold text-gray-900">DAZZA</span>
            </div>
            <button 
              onClick={onClose}
              className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => onClose()}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    active 
                      ? 'bg-orange-50 text-orange-600' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.name}
                  {active && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
            <button
              onClick={() => { openGuide(); onClose(); }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-orange-600 border border-dashed border-orange-300 hover:bg-orange-50 transition-colors w-full mt-2"
            >
              <BookOpen className="w-5 h-5" />
              Guide
            </button>
          </nav>

          {/* Build Version Footer */}
          <div className="p-4 border-t border-gray-200">
            <div className="text-center">
              <p className="text-xs text-gray-500">
                v{BUILD_INFO.version} • Build #{BUILD_INFO.buildNumber}
              </p>
              {BUILD_INFO.buildTime && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {formatBuildTime(BUILD_INFO.buildTime)}
                </p>
              )}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
